import { Tooltip } from '@/components/atoms/Tooltip';
import Button from '@/components/Button/newButtons';
import CopyToClipboard from '@/components/CopyToClipboard/CopyToClipboard';
import { useUserData } from '@/modules/activity-reports/src/hooks/useUserData';
import { dashboardOpenEmailPreview } from '@/modules/dashboard/dashboardActions';
import type { Business, ContactType } from '@/modules/dashboard/dashboardTypes';
import type { DirectEmail } from '@/modules/email/emailTypes';
import { useFreeTrialModal } from '@/modules/freeTrial/FreeTrialModalContext';
import { UserRole } from '@/modules/user/userTypes';
import {
	CheckMarkGreen,
	Clock,
	DropDownGold,
	EnvelopeCommunicationsFilled,
	ExclamationMarkOctagon,
	ExclamationMarkRedUnfilled,
	Instagram,
	Messenger,
	PhoneContactIcon,
	PlusGold,
	ScheduleIconNoClock
} from '@/resources/assets/images';
import type { RootState } from '@/rootReducer';
import encodeUrl from '@/utils/encodeUrl';
import { ComponentPropsWithoutRef, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { TrialBlurModal } from '../../../../atoms/TrialBlurModal';

const HiddenTooltip = () => (
	<Tooltip.Provider>
		<Tooltip.Root>
			<Tooltip.Trigger asChild>
				<Button
					variant="secondary"
					size="large"
					card
					className="absolute right-2 top-1/2 -translate-y-1/2"
				>
					Why is this hidden?
				</Button>
			</Tooltip.Trigger>
			<Tooltip.Content side="bottom" className="bg-transparent px-0 py-0">
				<TrialBlurModal isOpen />
			</Tooltip.Content>
		</Tooltip.Root>
	</Tooltip.Provider>
);

type EmailContactTabProps = Pick<Business, 'emailStatus'> &
	Pick<CommunicationSessionProps, 'onSetAsContacted' | 'openComposeEmail'> & {
		uniqueEmails: Array<{
			email: string;
			key: string;
			checked: boolean;
		}>;
		isScheduled: boolean;
		isUserFreeTier: boolean;
	};

type OnComposeEmailProps = Pick<DirectEmail, 'email'> & {
	type: keyof ContactType;
};

const EmailContactTab = ({
	emailStatus,
	uniqueEmails,
	isScheduled,
	isUserFreeTier,
	onSetAsContacted,
	openComposeEmail
}: EmailContactTabProps) => {
	const { openFreeTrialModal } = useFreeTrialModal();

	const dispatch = useDispatch();

	const openEmailPreview = () => dispatch(dashboardOpenEmailPreview());

	const handleComposeEmail = ({ type, email }: OnComposeEmailProps) => {
		if (isUserFreeTier) {
			openFreeTrialModal();
			return;
		}

		openComposeEmail({ email });
		onSetAsContacted(type);
	};

	const handleOpenEmailPreview = () => {
		openEmailPreview();
	};

	return (
		<ul className="flex w-full flex-col gap-2">
			{uniqueEmails.map(({ email, key, checked }) => (
				<li
					className="relative flex w-full flex-col gap-2 rounded-md hover:bg-neutral-700"
					key={email}
				>
					<div className="flex w-full flex-col gap-2">
						<Tooltip.Provider>
							<Tooltip.Root>
								<Tooltip.Trigger asChild>
									<button
										type="button"
										data-freetier={isUserFreeTier}
										className="relative flex cursor-pointer items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap break-words p-2 text-start align-top font-barlow font-normal text-dm-white data-[freetier=true]:blur-md md:max-w-none md:text-lg"
										onClick={() =>
											handleComposeEmail({
												email,
												type: key as keyof ContactType
											})
										}
									>
										{email}

										{checked && (
											<span className="flex h-full items-center justify-center gap-1 text-dm-green">
												<CheckMarkGreen className="h-auto w-4" />
												<p className="hidden md:block">
													Contacted
												</p>
											</span>
										)}
									</button>
								</Tooltip.Trigger>

								<Tooltip.Content
									data-freetier={isUserFreeTier}
									className="data-[freetier=true]:invisible"
								>
									{email}
								</Tooltip.Content>
							</Tooltip.Root>
						</Tooltip.Provider>

						{emailStatus === 'queued' && !isScheduled && (
							<span className="absolute right-[17px] top-[17px] flex items-center text-sm font-light text-dm-mid-grey">
								<Clock className="mr-[5px] w-4 [&_path]:fill-dm-mid-grey" />
								Queued to send
							</span>
						)}

						{emailStatus === 'failed' && (
							<span className="absolute right-[17px] top-[17px] flex items-center text-xs font-light text-dm-pure-white">
								<ExclamationMarkRedUnfilled className="mr-1 mt-0.5 w-4" />
								Failed to send&nbsp;
								<button
									type="button"
									className="cursor-pointer font-medium text-dm-gold"
									onClick={() =>
										handleComposeEmail({
											email,
											type: key as keyof ContactType
										})
									}
								>
									Retry
								</button>
							</span>
						)}
					</div>

					{isScheduled && (
						<button
							type="button"
							className="absolute right-[15px] top-3 grid h-[30px] w-[unset] cursor-default grid-flow-col items-center justify-center gap-x-2.5 rounded-[15px] bg-dm-blue2 px-[9px] py-0 text-center text-sm font-normal leading-7 text-[#231f20] hover:opacity-70 md:w-[124px]"
							onClick={handleOpenEmailPreview}
						>
							<ScheduleIconNoClock />
						</button>
					)}

					{isUserFreeTier && <HiddenTooltip />}
				</li>
			))}
		</ul>
	);
};

type PhoneWrapperComponentProps = ComponentPropsWithoutRef<'a'> & {
	isUserFreeTier?: boolean;
};

const PhoneWrapperComponent = ({
	isUserFreeTier,
	...props
}: PhoneWrapperComponentProps) => {
	const WrapperComponent = isUserFreeTier ? 'span' : 'a';

	return (
		<WrapperComponent
			{...props}
			className="flex cursor-pointer items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap break-words p-2 text-start align-top font-barlow text-lg font-normal text-dm-white md:max-w-none"
		/>
	);
};

type PhoneContactTabProps = Pick<
	CommunicationSessionProps,
	'onSetAsContacted'
> & {
	uniquePhones: Array<{
		phone: string;
		checked: boolean;
	}>;
	isUserFreeTier: boolean;
};

const PhoneContactTab = ({
	uniquePhones = [],
	isUserFreeTier,
	onSetAsContacted
}: PhoneContactTabProps) => {
	const { openFreeTrialModal } = useFreeTrialModal();

	const handleToggleContacted = () => {
		if (isUserFreeTier) {
			openFreeTrialModal();
			return;
		}

		onSetAsContacted('phone');
	};

	return (
		<ul className="flex w-full flex-col gap-2">
			{uniquePhones.map(({ phone, checked }) => (
				<li
					className="group/phone relative flex w-full flex-col gap-2 rounded-md hover:bg-neutral-700"
					key={phone}
				>
					<button
						type="button"
						onClick={handleToggleContacted}
						data-freetier={isUserFreeTier}
						className="flex w-full items-center gap-2 data-[freetier=true]:blur-md"
					>
						<PhoneWrapperComponent
							isUserFreeTier={isUserFreeTier}
							href={isUserFreeTier ? undefined : `tel:${phone}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{phone}

							{checked && (
								<span className="flex h-full items-center justify-center gap-1 text-dm-green">
									<CheckMarkGreen className="h-auto w-4" />
									<p className="hidden md:block">Contacted</p>
								</span>
							)}
						</PhoneWrapperComponent>

						{!isUserFreeTier && (
							<section className="hidden border-2 border-transparent opacity-0 transition-opacity group-hover/phone:inline-block group-hover/phone:opacity-100">
								<CopyToClipboard
									textToCopy={phone}
									type="Phone number"
								/>
							</section>
						)}
					</button>

					{isUserFreeTier && <HiddenTooltip />}
				</li>
			))}
		</ul>
	);
};

type CommunicationSessionTabs = 'email' | 'phone' | undefined;

export type CommunicationSessionProps = Pick<
	Business,
	| '_id'
	| 'email'
	| 'emailLowPriority'
	| 'phone'
	| 'facebook'
	| 'web'
	| 'userAddedData'
	| 'emailStatus'
	| 'socials'
	| 'contactType'
> & {
	openAddDataModal: VoidFunction;
	openReportIncorrectDataModal: VoidFunction;
	onSetAsContacted: (type: keyof ContactType) => void;
	openComposeEmail: ({ email }: Pick<DirectEmail, 'email'>) => void;
};

export const CommunicationSession = ({
	_id,
	email,
	phone,
	facebook,
	web,
	userAddedData,
	emailStatus,
	socials,
	contactType,
	openAddDataModal,
	openReportIncorrectDataModal,
	onSetAsContacted,
	openComposeEmail
}: CommunicationSessionProps) => {
	const { user } = useUserData();

	const { openFreeTrialModal } = useFreeTrialModal();

	const isUserFreeTier =
		user?.role === UserRole.Free || user?.role === UserRole.PreTrial;

	const scheduledEmailActivity = useSelector(
		(state: RootState) =>
			state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
		shallowEqual
	);

	const [activeTab, setActiveTab] =
		useState<CommunicationSessionTabs>(undefined);

	const handleChangeActiveTab = (
		tab: NonNullable<CommunicationSessionTabs>
	) => {
		setActiveTab(state => {
			if (state === tab) return undefined;

			return tab;
		});
	};

	const isActiveTabNonUndefined = useMemo(
		() => activeTab !== undefined,
		[activeTab]
	);

	const encodedMessengerUrl = useMemo(
		() =>
			facebook?.messengerUrl
				? encodeUrl(
						facebook.messengerUrl.startsWith('http://') ||
							facebook.messengerUrl.startsWith('https://')
							? facebook.messengerUrl
							: `http://${facebook.messengerUrl}`
					)
				: undefined,
		[facebook?.messengerUrl]
	);

	const encodedInstagramUrl = useMemo(
		() =>
			socials?.instagram
				? encodeUrl(
						socials?.instagram.startsWith('http://') ||
							socials?.instagram.startsWith('https://')
							? socials?.instagram
							: `http://${socials?.instagram}`
					)
				: undefined,
		[socials?.instagram]
	);

	const handleOpenAddDataModal = () => {
		if (isUserFreeTier) {
			openFreeTrialModal();
			return;
		}

		openAddDataModal();
	};

	const handleOpenReportDataModal = () => {
		if (isUserFreeTier) {
			openFreeTrialModal();
			return;
		}

		openReportIncorrectDataModal();
	};

	const isScheduled = useMemo(() => {
		return Boolean(scheduledEmailActivity[_id]);
	}, [scheduledEmailActivity, _id]);

	const uniqueEmails = useMemo(() => {
		const mappedUserCustomData = userAddedData?.map(
			(customEmail, index) => ({
				...customEmail,
				email: customEmail.data,
				key: String(index)
			})
		);

		const filteredUserCustomData =
			mappedUserCustomData?.filter(
				customEmail =>
					customEmail?.email && customEmail.type === 'email'
			) || [];

		const filteredEmails = [
			{
				email: facebook?.email || '',
				key: 'emailPrimary',
				checked: contactType?.emailPrimary || false
			},
			{
				email: email || '',
				key: 'emailSecondary',
				checked: contactType?.emailSecondary || false
			},
			{
				email: web?.email || '',
				key: 'thirdEmail',
				checked: contactType?.thirdEmail || false
			}
		];

		const emails = [...filteredEmails, ...filteredUserCustomData]
			.map(customData => ({
				email: customData.email,
				key: customData.key,
				checked: customData.checked ?? false
			}))
			.filter(emailItem => emailItem.email.trim().length > 0);

		return emails.reduce(
			(acc, current) => {
				if (current.email && !acc.seen.has(current.email)) {
					acc.seen.add(current.email);
					acc.result.push(current);
				}
				return acc;
			},
			{ seen: new Set<string>(), result: [] as typeof emails }
		).result;
	}, [
		facebook?.email,
		contactType?.emailPrimary,
		contactType?.emailSecondary,
		contactType?.thirdEmail,
		email,
		web?.email,
		userAddedData
	]);

	const uniquePhones = useMemo(() => {
		const phonesArray = phone?.display
			? [
					{
						phone: phone.display,
						checked: contactType?.phone || false
					}
				]
			: [];

		userAddedData?.forEach(customPhone => {
			if (customPhone?.data && customPhone.type === 'phone') {
				phonesArray.push({
					phone: customPhone.data,
					checked: customPhone.checked || false
				});
			}
		});

		const seen = new Set<string>();

		return phonesArray.filter(entry => {
			const phoneValue = entry.phone;

			if (!phoneValue || seen.has(phoneValue)) return false;

			seen.add(phoneValue);

			return true;
		});
	}, [contactType?.phone, phone?.display, userAddedData]);

	useEffect(() => {
		if (activeTab === 'email' && uniqueEmails.length === 0) {
			setActiveTab(undefined);
		}

		if (activeTab === 'phone' && uniquePhones.length === 0) {
			setActiveTab(undefined);
		}
	}, [activeTab, uniqueEmails.length, uniquePhones.length]);

	const hasEmailContacted = useMemo(
		() =>
			contactType?.emailPrimary ||
			contactType?.emailSecondary ||
			contactType?.thirdEmail,
		[
			contactType?.emailPrimary,
			contactType?.emailSecondary,
			contactType?.thirdEmail
		]
	);

	const hasPhoneContacted = useMemo(
		() => contactType?.phone,
		[contactType?.phone]
	);

	const hasMessengerContacted = useMemo(
		() => contactType?.messenger,
		[contactType?.messenger]
	);

	const hasInstagramContacted = useMemo(
		() => contactType?.instagramMessenger,
		[contactType?.instagramMessenger]
	);

	return (
		<div
			data-is-active={isActiveTabNonUndefined}
			className="flex w-[calc(100%+0.125rem)] -translate-x-[0.0625rem] flex-col items-start rounded-lg border border-dm-grey px-2 py-2.5 transition-colors data-[is-active=true]:border-dm-gold"
		>
			<header className="flex w-full flex-wrap items-center justify-evenly gap-3 md:flex-nowrap md:justify-between">
				<button
					type="button"
					onClick={() =>
						uniqueEmails.length > 0 &&
						handleChangeActiveTab('email')
					}
					disabled={uniqueEmails.length === 0}
					data-is-active={activeTab === 'email'}
					className="group flex h-9 cursor-pointer items-center gap-1.5 rounded-md bg-transparent transition-colors disabled:cursor-not-allowed data-[is-active=true]:bg-dm-black3 [&:not(:disabled)]:hover:bg-dm-black3"
				>
					<span className="flex items-center">
						<p className="flex items-center gap-2 font-semibold text-dm-gold group-disabled:text-dm-mid-grey">
							<EnvelopeCommunicationsFilled className="inline-block h-6 w-auto [&_path]:fill-dm-gold group-disabled:[&_path]:fill-dm-mid-grey" />
							Email
						</p>

						{hasEmailContacted && (
							<CheckMarkGreen className="flex h-full w-3 -translate-y-1/2 items-start" />
						)}
					</span>

					<DropDownGold className="inline-block w-4 rotate-180 transition-transform group-data-[is-active=true]:rotate-0 group-disabled:[&_path]:fill-dm-mid-grey" />
				</button>

				<button
					type="button"
					onClick={() =>
						uniquePhones.length > 0 &&
						handleChangeActiveTab('phone')
					}
					disabled={uniquePhones.length === 0}
					data-is-active={activeTab === 'phone'}
					className="group flex h-9 cursor-pointer items-center gap-1.5 rounded-md bg-transparent transition-colors disabled:cursor-not-allowed data-[is-active=true]:bg-dm-black3 [&:not(:disabled)]:hover:bg-dm-black3"
				>
					<span className="flex items-center">
						<p className="flex items-center gap-2 font-semibold text-dm-gold group-disabled:text-dm-mid-grey">
							<PhoneContactIcon className="inline-block h-5 w-auto group-disabled:[&_path]:fill-dm-mid-grey" />
							Phone
						</p>

						{hasPhoneContacted && (
							<CheckMarkGreen className="flex h-full w-3 -translate-y-1/2 items-start" />
						)}
					</span>

					<DropDownGold className="inline-block w-4 rotate-180 transition-transform group-data-[is-active=true]:rotate-0" />
				</button>

				<Tooltip.Provider>
					<Tooltip.Root>
						<Tooltip.Trigger>
							<a
								href={encodedMessengerUrl}
								data-is-disabled={!encodedMessengerUrl}
								onClick={() => {
									if (!encodedMessengerUrl) return null;

									onSetAsContacted('messenger');
								}}
								target="_blank"
								rel="noopener noreferrer"
								className="group relative flex h-9 cursor-pointer items-center gap-1.5 rounded-md bg-transparent px-3 text-lg font-semibold text-dm-gold transition-colors hover:bg-dm-black3 data-[is-disabled=true]:cursor-not-allowed data-[is-disabled=true]:text-dm-mid-grey"
							>
								<span className="flex items-center">
									<p className="flex items-center gap-2 font-semibold text-dm-gold group-disabled:text-dm-mid-grey">
										<Messenger className="inline-block h-5 w-auto group-disabled:[&_path]:fill-dm-mid-grey" />
										FB
									</p>

									{hasMessengerContacted && (
										<CheckMarkGreen className="flex h-full w-3 -translate-y-1/2 items-start" />
									)}
								</span>
							</a>
						</Tooltip.Trigger>
						<Tooltip.Content>Facebook Messenger</Tooltip.Content>
					</Tooltip.Root>
				</Tooltip.Provider>

				<Tooltip.Provider>
					<Tooltip.Root>
						<Tooltip.Trigger asChild>
							<a
								href={encodedInstagramUrl}
								data-is-disabled={!encodedInstagramUrl}
								onClick={() => {
									if (!encodedInstagramUrl) return null;

									onSetAsContacted('instagramMessenger');
								}}
								target="_blank"
								rel="noopener noreferrer"
								className="group relative flex h-9 cursor-pointer items-center gap-1.5 rounded-md bg-transparent px-3 text-lg font-semibold text-dm-gold transition-colors hover:bg-dm-black3 data-[is-disabled=true]:cursor-not-allowed data-[is-disabled=true]:text-dm-mid-grey"
							>
								<span className="flex items-center">
									<p className="flex items-center gap-2 font-semibold text-dm-gold group-disabled:text-dm-mid-grey">
										<Instagram className="inline-block h-5 w-auto group-disabled:[&_path]:fill-dm-mid-grey" />
										IG
									</p>

									{hasInstagramContacted && (
										<CheckMarkGreen className="flex h-full w-3 -translate-y-1/2 items-start" />
									)}
								</span>
							</a>
						</Tooltip.Trigger>
						<Tooltip.Content>Instagram Messenger</Tooltip.Content>
					</Tooltip.Root>
				</Tooltip.Provider>
			</header>

			<section
				data-is-active={isActiveTabNonUndefined}
				className="flex h-0 w-full origin-top scale-y-0 flex-col gap-2 transition-[transform,height,margin] data-[is-active=true]:mt-3 data-[is-active=true]:h-auto data-[is-active=true]:scale-y-100"
			>
				<div className="flex flex-col items-start gap-2 border-y border-dm-gold py-2">
					{activeTab === 'email' && (
						<EmailContactTab
							uniqueEmails={uniqueEmails}
							isScheduled={isScheduled}
							emailStatus={emailStatus}
							isUserFreeTier={isUserFreeTier}
							onSetAsContacted={onSetAsContacted}
							openComposeEmail={openComposeEmail}
						/>
					)}

					{activeTab === 'phone' && (
						<PhoneContactTab
							uniquePhones={uniquePhones}
							isUserFreeTier={isUserFreeTier}
							onSetAsContacted={onSetAsContacted}
						/>
					)}
				</div>

				<div className="flex flex-col items-start gap-2">
					<button
						type="button"
						className="relative flex w-full cursor-pointer items-center gap-2.5 rounded-md p-2 text-start text-lg text-dm-white transition-colors hover:bg-neutral-700"
						onClick={handleOpenAddDataModal}
					>
						<PlusGold className="inline-block w-8" />
						Add Contact Data
					</button>

					<button
						type="button"
						className="relative flex w-full cursor-pointer items-center gap-2.5 rounded-md p-2 text-start text-lg text-dm-white transition-colors hover:bg-neutral-700"
						onClick={handleOpenReportDataModal}
					>
						<ExclamationMarkOctagon className="inline-block w-8" />
						Report Incorrect Data
					</button>
				</div>
			</section>
		</div>
	);
};
